//src/hooks/useClientCompanyContext.ts
import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { isTenantAdminUser } from "@/lib/permissions";
import { listAvailableClientCompanies, type ClientCompanyListItem } from "@/services/clientCompanies";
import {
  clearSelectedClientCompany,
  setSelectedClientCompany,
  useSelectedClientCompany,
} from "@/hooks/useSelectedClientCompany";


let automaticClientContextChangeInProgress = false;

export function useClientCompanyContext() {
  const { user, refreshMe, runContextSwitch, isContextSwitching } = useAuth();
  const { selectedClientCompanyId } = useSelectedClientCompany();
  const isTenantAdmin = isTenantAdminUser(user);

  const isPlatformUser = user?.access_scope === "platform" || user?.context_type === "platform";

  const availableClientsQ = useQuery({
    queryKey: ["client_companies", "available", user?.tenant_id ?? null, user?.context_type ?? null],
    queryFn: () => listAvailableClientCompanies(),
    enabled: !!user && !isPlatformUser,
    staleTime: 15_000,
  });


  useEffect(() => {
    if (!isPlatformUser) return;
    clearSelectedClientCompany();
  }, [isPlatformUser]);

  const activeClientCompanies = useMemo(
    () => (availableClientsQ.data ?? []).filter((client) => client.is_active),
    [availableClientsQ.data],
  );

  const selectedClientCompany = useMemo(
    () => activeClientCompanies.find((client) => client.id === selectedClientCompanyId) ?? null,
    [activeClientCompanies, selectedClientCompanyId],
  );

  const clearClientCompany = useCallback(async () => {
    clearSelectedClientCompany();
    await refreshMe();
  }, [refreshMe]);

  const selectClientCompany = useCallback(async (client: ClientCompanyListItem) => {
    setSelectedClientCompany({ id: client.id, name: client.name });
    await refreshMe();
  }, [refreshMe]);

  const refreshClientCompanies = useCallback(async () => {
    await availableClientsQ.refetch();
  }, [availableClientsQ]);

  useEffect(() => {
    // Validacoes automaticas da carteira pertencem exclusivamente ao contexto
    // da Empresa. Durante Tenant -> Client, o /me ja pode ter mudado para
    // context_type=client enquanto a query da carteira reinicia. Nesse intervalo
    // selectedClientCompany fica temporariamente null; isso nao significa que o
    // Cliente selecionado seja invalido e, portanto, ele nunca deve ser limpo.
    if (
      !user ||
      user.context_type !== "tenant" ||
      isContextSwitching ||
      availableClientsQ.isLoading ||
      availableClientsQ.isFetching ||
      !availableClientsQ.isFetched
    ) {
      return;
    }

    if (selectedClientCompanyId && !selectedClientCompany) {
      if (automaticClientContextChangeInProgress) return;
      automaticClientContextChangeInProgress = true;

      void runContextSwitch(async () => {
        clearSelectedClientCompany();
        await refreshMe();
      }).finally(() => {
        automaticClientContextChangeInProgress = false;
      });
      return;
    }

    if (!isTenantAdmin && activeClientCompanies.length === 1 && !selectedClientCompanyId) {
      if (automaticClientContextChangeInProgress) return;
      automaticClientContextChangeInProgress = true;

      void runContextSwitch(async () => {
        setSelectedClientCompany({
          id: activeClientCompanies[0].id,
          name: activeClientCompanies[0].name,
        });
        await refreshMe();
      }).finally(() => {
        automaticClientContextChangeInProgress = false;
      });
    }
  }, [
    activeClientCompanies,
    availableClientsQ.isFetched,
    availableClientsQ.isFetching,
    availableClientsQ.isLoading,
    isContextSwitching,
    isTenantAdmin,
    selectedClientCompany,
    selectedClientCompanyId,
    refreshMe,
    runContextSwitch,
    user,
  ]);

  useEffect(() => {
    const handleRefresh = () => {
      void availableClientsQ.refetch();
    };

    window.addEventListener("focus", handleRefresh);
    window.addEventListener("normex:client-companies-changed", handleRefresh);

    return () => {
      window.removeEventListener("focus", handleRefresh);
      window.removeEventListener("normex:client-companies-changed", handleRefresh);
    };
  }, [availableClientsQ]);

  return {
    activeClientCompanies,
    availableClients: activeClientCompanies,
    clearClientCompany,
    hasSelectedClientCompany: !!selectedClientCompany,
    hasLoadedClientCompanies: availableClientsQ.isFetched,
    isLoadingClientCompanies: availableClientsQ.isLoading,
    isRefreshingClientCompanies: availableClientsQ.isFetching && !availableClientsQ.isLoading,
    isTenantAdmin,
    refreshClientCompanies,
    selectClientCompany,
    selectedClientCompany,
    selectedClientCompanyId: selectedClientCompany?.id ?? null,
  };
}
